// frontend/src/pages/RequestNewServicePage.tsx
// Mobile-first page to request new services that aren't available

import React, { useState } from 'react';
import { ArrowLeft, Send, MessageSquare, Lightbulb } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../utils/toast';
import { useServiceFilterOptions } from '@/hooks/useProviderServicesQueries';
import LoadingSpinner from '@/components/LoadingSpinner';

interface ServiceRequestFormData {
  service_name: string;
  category_id?: number;
  description: string;
  reason: string;
  expected_price_range?: string;
  estimated_duration?: string;
  priority: 'low' | 'medium' | 'high';
  contact_preference: 'email' | 'phone' | 'either';
}

const RequestNewServicePage: React.FC = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState<ServiceRequestFormData>({
    service_name: '',
    category_id: undefined,
    description: '',
    reason: '',
    expected_price_range: '',
    estimated_duration: '',
    priority: 'medium',
    contact_preference: 'email',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get filter options for categories
  const { data: filterOptions } = useServiceFilterOptions();
  const categories = filterOptions?.categories || [];

  // Handle form input changes
  const handleInputChange = (field: keyof ServiceRequestFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Form validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.service_name.trim()) {
      newErrors.service_name = 'Service name is required';
    } else if (formData.service_name.length > 255) {
      newErrors.service_name = 'Service name must be less than 255 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 50) {
      newErrors.description = 'Description must be at least 50 characters';
    } else if (formData.description.length > 1000) {
      newErrors.description = 'Description must be less than 1000 characters';
    }

    if (!formData.reason.trim()) {
      newErrors.reason = 'Reason for request is required';
    } else if (formData.reason.length < 20) {
      newErrors.reason = 'Please provide a more detailed reason (at least 20 characters)';
    } else if (formData.reason.length > 500) {
      newErrors.reason = 'Reason must be less than 500 characters';
    }

    if (formData.expected_price_range && formData.expected_price_range.length > 100) {
      newErrors.expected_price_range = 'Price range must be less than 100 characters';
    }

    if (formData.estimated_duration && formData.estimated_duration.length > 50) {
      newErrors.estimated_duration = 'Duration must be less than 50 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setIsSubmitting(true);

    try {
      // TODO: Replace with actual API call to submit service request
      // const response = await serviceRequestAPI.submitRequest(formData);
      
      // Simulate API call for now
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate success
      toast.success('Service request submitted successfully! We will review your request and get back to you within 2-3 business days.');
      navigate('/provider-services');
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit service request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle navigation with unsaved changes
  const handleBack = () => {
    const hasUnsavedChanges = Object.values(formData).some(value => 
      typeof value === 'string' ? value.trim() !== '' : value !== undefined
    );

    if (hasUnsavedChanges) {
      const confirmMessage = 'You have unsaved changes. Are you sure you want to leave?';
      if (window.confirm(confirmMessage)) {
        navigate('/provider-services');
      }
    } else {
      navigate('/provider-services');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-4xl px-4 py-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={handleBack}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
                Request New Service
              </h1>
              <p className="text-sm text-gray-600 sm:text-base">
                Can't find the service you offer? Request it here
              </p>
            </div>
          </div>

          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Lightbulb className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Before submitting a request:</p>
                <ul className="space-y-1 text-blue-700">
                  <li>• Check if a similar service already exists in our browse section</li>
                  <li>• Provide detailed information to help us understand your needs</li>
                  <li>• We typically respond within 2-3 business days</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-4 sm:p-6">
            <div className="space-y-6">
              {/* Service Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-blue-600" />
                  Service Information
                </h3>
                
                <div className="space-y-4">
                  {/* Service Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service Name *
                    </label>
                    <input
                      type="text"
                      value={formData.service_name}
                      onChange={(e) => handleInputChange('service_name', e.target.value)}
                      placeholder="e.g., Advanced Pool Cleaning, Custom Web Development, Pet Grooming"
                      className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.service_name ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.service_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.service_name}</p>
                    )}
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      value={formData.category_id || ''}
                      onChange={(e) => handleInputChange('category_id', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a category (optional)</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-sm text-gray-500">
                      Choose the category that best fits your service
                    </p>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Detailed Service Description *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Describe the service you want to offer in detail. Include what's included, what makes it unique, and any special requirements..."
                      rows={5}
                      className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${
                        errors.description ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.description && (
                      <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      {formData.description.length}/1000 characters (minimum 50 required)
                    </p>
                  </div>

                  {/* Service Details Grid */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {/* Expected Price Range */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expected Price Range
                      </label>
                      <input
                        type="text"
                        value={formData.expected_price_range}
                        onChange={(e) => handleInputChange('expected_price_range', e.target.value)}
                        placeholder="e.g., $50-100, $200-500, Contact for quote"
                        className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.expected_price_range ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {errors.expected_price_range && (
                        <p className="mt-1 text-sm text-red-600">{errors.expected_price_range}</p>
                      )}
                    </div>

                    {/* Estimated Duration */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Estimated Duration
                      </label>
                      <input
                        type="text"
                        value={formData.estimated_duration}
                        onChange={(e) => handleInputChange('estimated_duration', e.target.value)}
                        placeholder="e.g., 2 hours, 1-2 days, 1 week"
                        className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.estimated_duration ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {errors.estimated_duration && (
                        <p className="mt-1 text-sm text-red-600">{errors.estimated_duration}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Request Details */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Request Details
                </h3>
                
                <div className="space-y-4">
                  {/* Reason for Request */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Why do you need this service added? *
                    </label>
                    <textarea
                      value={formData.reason}
                      onChange={(e) => handleInputChange('reason', e.target.value)}
                      placeholder="Explain why this service is important for your business and customers. Include market demand, unique value proposition, or special circumstances..."
                      rows={4}
                      className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${
                        errors.reason ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.reason && (
                      <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      {formData.reason.length}/500 characters (minimum 20 required)
                    </p>
                  </div>

                  {/* Priority and Contact Preference Grid */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {/* Priority */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Priority Level
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) => handleInputChange('priority', e.target.value as 'low' | 'medium' | 'high')}
                        className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="low">Low - Nice to have</option>
                        <option value="medium">Medium - Would help my business</option>
                        <option value="high">High - Essential for my business</option>
                      </select>
                    </div>

                    {/* Contact Preference */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Preference
                      </label>
                      <select
                        value={formData.contact_preference}
                        onChange={(e) => handleInputChange('contact_preference', e.target.value as 'email' | 'phone' | 'either')}
                        className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="email">Email only</option>
                        <option value="phone">Phone call preferred</option>
                        <option value="either">Either email or phone</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Section */}
              <div className="pt-6 border-t border-gray-200">
                <div className="space-y-4">
                  {/* Expectations */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">What happens next?</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Our team will review your service request within 2-3 business days</li>
                      <li>• We may contact you for additional information or clarification</li>
                      <li>• If approved, the service will be added to our platform</li>
                      <li>• You'll receive an email notification about the status of your request</li>
                    </ul>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-3 sm:flex-row sm:justify-end sm:items-center">
                    <button
                      onClick={handleBack}
                      disabled={isSubmitting}
                      className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm font-medium"
                    >
                      Cancel
                    </button>
                    
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting || Object.keys(errors).length > 0}
                      className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Submitting Request...
                        </>
                      ) : (
                        <>
                          <Send size={16} />
                          Submit Request
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestNewServicePage;